import React, { useState, useEffect } from 'react';
import { useNavigate, Link as RouteLink } from 'react-router-dom';
import {
  Box,
  VStack,
  HStack,
  Text,
  Image,
  Alert,
  AlertIcon,
  AlertDescription,
  Divider,
  useColorModeValue,
  Container,
  Spinner,
  useToast,
} from '@chakra-ui/react';
import { FiLock, FiEye, FiEyeOff, FiMail, FiLogIn } from 'react-icons/fi';

import { Card, Button, FormField } from '@grcs/components';
import { SEO } from '@grcs/components/SEO';

import { useUserOperations } from '@grcs/hook/useDatabase';
import { useLocalStorage } from '@grcs/hook/useLocalStorage';
import { UserRole } from '@grcs/types/UserRole';
import { validateEmail } from '@grcs/utils/validation/validateEmail';
import { logger } from '@grcs/utils/logging/logger';
import { normalizedLanguage } from '@grcs/utils/language/normalizedLanguage';
import csrf_logo from '@grcs/images/csrf_logo.png';
import background_image from '@grcs/images/grcs_homepage.jpg';

interface LoginFormData {
  email: string;
  password: string;
}

interface LoginError {
  field?: 'email' | 'password' | 'general';
  message: string;
}

export const AdminLoginEnhanced: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const userOps = useUserOperations();
  const [, setUser] = useLocalStorage('user', {});
  const [, setUserLanguage] = useLocalStorage('user_language', {});
  const [storedAuthLanguage] = useLocalStorage('auth_language', 'en');
  const authLanguage = storedAuthLanguage as 'en' | 'zh';

  // Form state
  const [formData, setFormData] = useState<LoginFormData>({
    email: '',
    password: '',
  });

  // UI state
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<LoginError[]>([]);
  // const [rememberMe, setRememberMe] = useState(false);

  // Theme colors
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const overlayBg = useColorModeValue('blackAlpha.600', 'blackAlpha.800');
  const isChinese = authLanguage === 'zh';
  const copy = {
    title: isChinese ? '教师登录' : 'Teacher Login',
    demo: isChinese
      ? '演示账号（开发模式）'
      : 'Demo Credentials (Development Mode)',
    emailLabel: isChinese ? '邮箱地址' : 'Email Address',
    emailPlaceholder: isChinese
      ? '输入你的邮箱地址'
      : 'Enter your email address',
    passwordLabel: isChinese ? '密码' : 'Password',
    passwordPlaceholder: isChinese ? '输入你的密码' : 'Enter your password',
    signingIn: isChinese ? '登录中...' : 'Signing in...',
    signIn: isChinese ? '登录' : 'Sign In',
    studentLogin: isChinese ? '学生登录' : 'Student Login',
    terms: isChinese
      ? '登录即表示你同意我们的服务条款和隐私政策'
      : 'By signing in, you agree to our Terms of Service and Privacy Policy',
    footer: '© 2024 泓河开卷™ by Grand River Chinese School',
    emailRequired: isChinese ? '请输入邮箱地址' : 'Email is required',
    passwordRequired: isChinese ? '请输入密码' : 'Password is required',
    authFailed: isChinese ? '认证失败' : 'Authentication failed',
    userProfileMissing: isChinese ? '未找到用户资料' : 'User profile not found',
    loginSuccess: isChinese ? '登录成功' : 'Login successful',
    welcomeDescription: (name: string) =>
      isChinese ? `欢迎回来，${name}！` : `Welcome back, ${name}!`,
    unauthorized: isChinese
      ? '未授权：仅限教师或管理员访问'
      : 'Unauthorized: Staff access required (Teacher or Administrator)',
    unexpectedError: isChinese
      ? '发生了意外错误，请重试。'
      : 'An unexpected error occurred. Please try again.',
  };

  // Auto-fill demo credentials for development
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      setFormData({
        email: 'admin@grcs.com',
        password: '123456',
      });
    }
  }, []);

  // Validation
  const validateForm = (): boolean => {
    const newErrors: LoginError[] = [];
    const trimmedEmail = formData.email.trim();

    if (!trimmedEmail) {
      newErrors.push({ field: 'email', message: copy.emailRequired });
    } else {
      const emailError = validateEmail(trimmedEmail);
      if (emailError) {
        newErrors.push({ field: 'email', message: emailError });
      }
    }

    if (!formData.password) {
      newErrors.push({ field: 'password', message: copy.passwordRequired });
    }

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  // Authentication flow
  const fetchAuthData = async () => {
    const {
      data: { session },
      error,
    } = await userOps.getSession();
    if (error) throw error;
    return session?.user;
  };

  const fetchUserData = async (auth_id: string) => {
    const { data, error } = await userOps.getDataById(
      'user',
      'auth_uid',
      auth_id,
    );
    if (error) throw error;
    return data?.[0];
  };

  const handleLogin = async () => {
    if (!validateForm()) return;

    setIsLoading(true);
    setErrors([]);

    try {
      // Trim email to remove any trailing/leading spaces
      const trimmedEmail = formData.email.trim();

      // Sign in with email/password
      const { error: signInError } = await userOps.signInWithPassword(
        trimmedEmail,
        formData.password,
      );

      if (signInError) {
        // Log failed login attempt
        await logger.logUserLogin(trimmedEmail, false, {
          error: signInError.message,
          userAgent: navigator?.userAgent,
          attemptedRole: 'admin',
        });
        setErrors([{ field: 'general', message: signInError.message }]);
        return;
      }

      // Get authenticated user
      const authUser = await fetchAuthData();
      if (!authUser) {
        // Log authentication failure
        await logger.logUserLogin(trimmedEmail, false, {
          error: 'Authentication failed - no auth user',
          attemptedRole: 'admin',
        });
        setErrors([{ field: 'general', message: copy.authFailed }]);
        return;
      }

      // Get user profile
      const userProfile = await fetchUserData(authUser.id);
      if (!userProfile) {
        // Log profile not found
        await logger.logUserLogin(formData.email, false, {
          error: 'User profile not found',
          authId: authUser.id,
          attemptedRole: 'admin',
        });
        setErrors([{ field: 'general', message: copy.userProfileMissing }]);
        return;
      }

      let preferredLanguage = 'en';
      try {
        const languageResult = await userOps.getUserLanguageByType(
          userProfile.role,
        );
        preferredLanguage = normalizedLanguage(
          languageResult.data?.[0]?.language,
        );
      } catch (languageError) {
        console.warn(
          'Failed to load staff language preference.',
          languageError,
        );
      }
      setUserLanguage(preferredLanguage);

      // Set user data in localStorage
      setUser({
        ...userProfile,
        role: userProfile.role,
        isLogin: true,
        error: '',
        email: authUser.email,
        phone: authUser.phone,
        password: '',
      });

      // Show success toast
      toast({
        title: copy.loginSuccess,
        description: copy.welcomeDescription(
          userProfile.full_name || userProfile.first_name,
        ),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      // Navigate based on role
      if (userProfile.role === UserRole.ADMIN) {
        // Log successful admin login
        await logger.logUserLogin(formData.email, true, {
          role: userProfile.role,
          fullName: userProfile.full_name || userProfile.first_name,
          userId: userProfile.id,
        });
        navigate('/admin', { replace: true });
      } else if (userProfile.role === UserRole.TEACHER) {
        // Log successful teacher login
        await logger.logUserLogin(formData.email, true, {
          role: userProfile.role,
          fullName: userProfile.full_name || userProfile.first_name,
          userId: userProfile.id,
        });
        navigate('/teacher', { replace: true });
      } else {
        // Log unauthorized access attempt
        await logger.logUserLogin(formData.email, false, {
          error: 'Unauthorized role',
          actualRole: userProfile.role,
          attemptedRole: 'admin',
        });
        setErrors([
          {
            field: 'general',
            message: copy.unauthorized,
          },
        ]);
      }
    } catch (error: any) {
      console.error('Login error:', error);

      // Log system error during login
      await logger.logSystemError(
        `Login system error: ${error.message}`,
        'AdminLogin',
        {
          email: formData.email,
          errorStack: error.stack,
          timestamp: new Date().toISOString(),
        },
      );

      setErrors([
        {
          field: 'general',
          message: error.message || copy.unexpectedError,
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: keyof LoginFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear field-specific errors
    setErrors((prev) => prev.filter((error) => error.field !== field));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isLoading) {
      handleLogin();
    }
  };

  const getFieldError = (field: string) => {
    return errors.find((error) => error.field === field)?.message;
  };

  const generalError = errors.find((error) => error.field === 'general')
    ?.message;

  return (
    <>
      <SEO
        title="Staff Login"
        description="Grand River Chinese School staff login portal. Sign in to access your teacher or administrator dashboard."
        keywords="staff login, teacher login, admin login, Grand River Chinese School"
      />
      <Box
        h="100dvh"
        position="relative"
        display="flex"
        flexDirection="column"
        overflow="hidden"
      >
        <Box
          h="95%"
          display="flex"
          justifyContent="center"
          backgroundImage={`url(${background_image})`}
          backgroundSize="cover"
          backgroundPosition="center"
        >
          <Container
            maxW="6xl"
            w={{ base: '80%', md: '60%', lg: '50%' }}
            h="100%"
            px={{ base: 4, md: 8 }}
            display="flex"
            flexDirection="column"
          >
            <Box
              flex="0 0 auto"
              minH={{ base: '120px', md: '20%' }}
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <VStack spacing={2} textAlign="center">
                <Box
                  display="flex"
                  flexDirection={{ base: 'column', md: 'row' }}
                  alignItems="center"
                  justifyContent="center"
                  gap={{ base: 3, md: 6 }}
                >
                  <Image
                    src={csrf_logo}
                    alt="School Logo"
                    h={{ base: '48px', md: '60px' }}
                  />
                  <Text
                    fontSize="4xl"
                    fontWeight="bold"
                    color="black.600"
                    display="inline-block"
                    whiteSpace="nowrap"
                    lineHeight="1"
                    flexShrink={0}
                  >
                    泓河开卷&trade;
                  </Text>
                </Box>
              </VStack>
            </Box>

            <Box
              flex="1"
              minH={0}
              display="flex"
              alignItems="flex-start"
              justifyContent="center"
            >
              <Card
                w="100%"
                maxH="100%"
                overflowY="auto"
                bg={cardBg}
                borderColor={borderColor}
                shadow="2xl"
                borderRadius="2xl"
                borderWidth="1px"
                px={{ base: 6, md: 10 }}
                py={{ base: 6, md: 8 }}
              >
                <VStack spacing={6} align="stretch">
                  <VStack spacing={2} textAlign="center">
                    <Text fontSize="2xl" fontWeight="bold" color="black.700">
                      {copy.title}
                    </Text>
                  </VStack>
                  {/* Demo Credentials Alert */}
                  {process.env.NODE_ENV === 'development' && (
                    <Alert status="info" borderRadius="md">
                      <AlertIcon />
                      <VStack align="start" spacing={1} fontSize="sm">
                        <AlertDescription fontWeight="semibold">
                          {copy.demo}
                        </AlertDescription>
                        <Text>Teacher: zhaobocong+4@hotmail.com / 123456</Text>
                      </VStack>
                    </Alert>
                  )}

                  {/* General Error */}
                  {generalError && (
                    <Alert status="error" borderRadius="md">
                      <AlertIcon />
                      <AlertDescription>{generalError}</AlertDescription>
                    </Alert>
                  )}

                  {/* Login Form */}
                  <VStack spacing={4} align="stretch">
                    <FormField
                      label={copy.emailLabel}
                      required
                      error={getFieldError('email')}
                    >
                      <Box position="relative">
                        <input
                          type="email"
                          placeholder={copy.emailPlaceholder}
                          value={formData.email}
                          onChange={(e) =>
                            handleInputChange('email', e.target.value)
                          }
                          onKeyPress={handleKeyPress}
                          disabled={isLoading}
                          style={{
                            paddingTop: '12px',
                            paddingBottom: '12px',
                            paddingLeft: '48px',
                            paddingRight: '12px',
                            width: '100%',
                            height: '48px',
                            border: `1px solid ${
                              getFieldError('email') ? 'red' : '#e2e8f0'
                            }`,
                            borderRadius: '6px',
                            boxSizing: 'border-box',
                            fontSize: '16px',
                          }}
                        />
                        <Box
                          position="absolute"
                          left="16px"
                          top="50%"
                          transform="translateY(-50%)"
                          color="gray.400"
                        >
                          <FiMail />
                        </Box>
                      </Box>
                    </FormField>

                    <FormField
                      label={copy.passwordLabel}
                      required
                      error={getFieldError('password')}
                    >
                      <Box position="relative">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          placeholder={copy.passwordPlaceholder}
                          value={formData.password}
                          onChange={(e) =>
                            handleInputChange('password', e.target.value)
                          }
                          onKeyPress={handleKeyPress}
                          disabled={isLoading}
                          style={{
                            paddingTop: '12px',
                            paddingBottom: '12px',
                            paddingLeft: '48px',
                            paddingRight: '48px',
                            width: '100%',
                            height: '48px',
                            border: `1px solid ${
                              getFieldError('password') ? 'red' : '#e2e8f0'
                            }`,
                            boxSizing: 'border-box',
                            borderRadius: '6px',
                            fontSize: '16px',
                          }}
                        />
                        <Box
                          position="absolute"
                          left="16px"
                          top="50%"
                          transform="translateY(-50%)"
                          color="gray.400"
                        >
                          <FiLock />
                        </Box>
                        <Box
                          position="absolute"
                          right="16px"
                          top="50%"
                          transform="translateY(-50%)"
                          cursor="pointer"
                          color="gray.400"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? <FiEyeOff /> : <FiEye />}
                        </Box>
                      </Box>
                    </FormField>

                    {/* Remember Me */}
                    {/* <HStack justify="space-between">
                  <HStack spacing={2}>
                    <input
                      type="checkbox"
                      id="remember"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                    />
                    <Text fontSize="sm" color="gray.600">
                      Remember me
                    </Text>
                  </HStack>
                  <RouteLink to="/auth/forgot-password">
                    <Text
                      fontSize="sm"
                      color="blue.500"
                      _hover={{ color: 'blue.600' }}
                    >
                      Forgot password?
                    </Text>
                  </RouteLink>
                </HStack> */}

                    {/* Login Button */}
                    <Button
                      leftIcon={isLoading ? <Spinner size="sm" /> : <FiLogIn />}
                      onClick={handleLogin}
                      isLoading={isLoading}
                      loadingText={copy.signingIn}
                      isDisabled={!formData.email || !formData.password}
                      size="lg"
                      width="100%"
                    >
                      {copy.signIn}
                    </Button>
                  </VStack>

                  <Divider />

                  {/* Additional Links */}
                  <VStack spacing={3} fontSize="sm" textAlign="center">
                    <HStack spacing={4}>
                      <RouteLink to="/auth/student-login">
                        <Text color="blue.500" _hover={{ color: 'blue.600' }}>
                          {copy.studentLogin}
                        </Text>
                      </RouteLink>
                    </HStack>

                    <Text color="gray.500" fontSize="xs">
                      {copy.terms}
                    </Text>
                  </VStack>
                </VStack>
              </Card>
            </Box>
          </Container>
        </Box>

        <Box
          h="10%"
          bg={overlayBg}
          borderTop="1px solid"
          borderTopColor="whiteAlpha.200"
          display="flex"
          alignItems="center"
        >
          <Container maxW="6xl" px={{ base: 4, md: 8 }}>
            <VStack spacing={2} textAlign="center">
              <Box
                display="flex"
                flexDirection={{ base: 'column', md: 'row' }}
                alignItems="center"
                justifyContent="center"
                gap={2}
              >
                <Image
                  src={csrf_logo}
                  alt="School Logo"
                  h={{ base: '20px', md: '24px' }}
                />
                <Text
                  color="white"
                  fontWeight="bold"
                  fontSize={{ base: 'sm', md: 'base' }}
                >
                  泓河开卷&trade;
                </Text>
              </Box>
              <VStack spacing={1}>
                <Text
                  color="whiteAlpha.900"
                  fontSize={{ base: 'xs', md: 'sm' }}
                  fontWeight="500"
                >
                  {copy.footer}
                </Text>
              </VStack>
            </VStack>
          </Container>
        </Box>
      </Box>
    </>
  );
};
